import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { DepartmentFormRule, DepartmentFormSchema } from "@core/form/form.rule";
import { useFormDefaultDepartment } from "@core/form/form-default-department.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateDepartment, 
  useQyGetDepartmentById, 
  useQyDeleteDepartment 
} from "@core/query/department.query";
import { GetResponseDepartmentModel } from "@core/model/query-model-department";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";

export function useEditDepartment() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultDepartment();
  const navigate = useNavigate();
  const { mapUpdateDepartment, mapDeleteDepartment } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { departmentId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Department
  const handleGetApiSuccess = (data: GetResponseDepartmentModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("head", responseData?.head || "");
    }
  };
  const {
    data: departmentResponse,
    isLoading,
    isError: departmentError,
  } = useQyGetDepartmentById(departmentId || "", handleGetApiSuccess);
  const departmentData = departmentResponse?.data?.[0];

  // API UPDATE Department
  const handleUpdateApiSuccess = () => {
    showSuccess("Department updated successfully");
  };
  const { mutate: updateDepartment, isLoading: isEditLoading } = useQyUpdateDepartment(
    handleUpdateApiSuccess
  );

  // API DELETE Department
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Department deleted successfully");
  };
  const { mutate: deleteDepartment } = useQyDeleteDepartment(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<DepartmentFormSchema>({
    defaultValues: getDefault(departmentData),
    resolver: zodResolver(DepartmentFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: DepartmentFormSchema) => {
    if (!departmentData) {
      throw new Error("No department data");
    }

    const formData = mapUpdateDepartment(form, departmentData.code);
    updateDepartment(formData);
  };
  const handleValidateError = (err: FieldErrors<DepartmentFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!departmentData) {
      throw new Error("No department data found");
    }

    const form = mapDeleteDepartment(departmentData.code);
    deleteDepartment(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    departmentId,
    departmentData,
    isLoading,
    departmentError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}